import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize } from 'rxjs/operators';
import { ClubService } from 'src/app/shared/club.service';
import { TranslationService } from 'src/app/shared/translation.service';
import { SortableFilter, SortDirection } from 'src/app/common/sortable-filter';
import { Honeycomb } from 'src/app/shared/honeycomb-api/honeycomb-api';
import { isNullOrWhitespace } from 'src/app/common/functions';

@Component({
    selector: 'hc-import-club-dialog',
    templateUrl: './import-club-dialog.component.html',
    styleUrls: []
})
export class ImportClubDialogComponent implements OnInit {

    public nameFormControl = new FormControl('', [Validators.required]);
    public clubs: Array<{ description: string; value: any }> = [];
    public clubSelectControl = new FormControl(this.clubs[0], [Validators.required]);

    public inProgress = false;
    public isError = false;

    public userNumbers: string[] = [];
    public userIDs: number[] = [];

    constructor(
        private dialogRef: MatDialogRef<ImportClubDialogComponent>,
        private trans: TranslationService,
        private clubService: ClubService,
        private snackbar: MatSnackBar,
        @Inject('UserController') private userController: Honeycomb.Tenant.Contact.IService.UserController,
    ) {
        this.clubs = [
            { description: this.trans.instant('admin.web.create-new-club'), value: -2 }
        ];
        this.clubSelectControl.setValue(this.clubs[0]);
    }

    ngOnInit() {
        this.inProgress = true;
        const filter = new SortableFilter('Name', SortDirection.asc);
        filter.pageSize = 1000;

        this.clubService.select(filter)
            .pipe(finalize(() => this.inProgress = false))
            .subscribe({
                next: (res) => {
                    (res?.items ?? []).forEach((club: any) => {
                        this.clubs.push({
                            description: club.name,
                            value: club.clubID
                        });
                    });
                },
                error: () => {
                    this.isError = true;
                }
            });
    }

    public isNameInputVisible(): boolean {
        return this.clubSelectControl.value === this.clubs[0];
    }

    public close() {
        this.dialogRef.close();
    }

    public async onFileSelected(event: Event) {
        this.userNumbers = [];
        this.userIDs = [];

        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) return;

        try {
            const ext = (file.name.split('.').pop() || '').toLowerCase();
            if (ext !== 'csv') {
                this.snackbar.open(this.trans.instant('admin.web.empty-file'), this.trans.instant('admin.web.close'));
                return;
            }

            const text = await file.text();
            this.userNumbers = this.parseCsvToUserNumbers(text);
            this.userNumbers = Array.from(new Set(this.userNumbers));

            if (this.userNumbers.length === 0) {
                this.snackbar.open(this.trans.instant('admin.web.empty-file'), this.trans.instant('admin.web.close'));
                return;
            }

            this.inProgress = true;
            this.userController.GetIds(this.userNumbers)
                .pipe(finalize(() => this.inProgress = false))
                .subscribe({
                    next: (rels) => {
                        const ids = (rels ?? []).map(r => (r as any).userID).filter((v: any) => typeof v === 'number');
                        this.userIDs = Array.from(new Set(ids));
                    },
                    error: (err) => {
                        this.isError = true;
                        console.error(err);
                    }
                });
        } finally {
            input.value = '';
        }
    }

    public confirm() {
        if (!this.userIDs || this.userIDs.length === 0) {
            this.snackbar.open(this.trans.instant('admin.web.no-record-selected'), this.trans.instant('admin.web.close'));
            return;
        }

        if (this.isNameInputVisible() && isNullOrWhitespace(this.nameFormControl.value)) {
            this.snackbar.open(this.trans.instant('admin.web.name-empty'), this.trans.instant('admin.web.close'));
            return;
        }

        this.inProgress = true;
        this.isError = false;

        if (this.isNameInputVisible()) {
            this.clubService.create(this.nameFormControl.value, this.userIDs)
                .pipe(finalize(() => this.inProgress = false))
                .subscribe({
                    next: () => {
                        this.snackbar.open(this.trans.instant('admin.web.list-created'), '', { duration: 2000 });
                        this.dialogRef.close(true);
                    },
                    error: (err) => {
                        this.isError = true;
                        console.error(err);
                        this.snackbar.open(this.trans.instant('admin.web.list-create-error'), '', { duration: 2000 });
                    }
                });
        } else {
            this.clubService.addToWhiteList(this.clubSelectControl.value.value, this.userIDs)
                .pipe(finalize(() => this.inProgress = false))
                .subscribe({
                    next: () => {
                        this.snackbar.open(this.trans.instant('admin.web.saved'), '', { duration: 2000 });
                        this.dialogRef.close(true);
                    },
                    error: (err) => {
                        this.isError = true;
                        console.error(err);
                        this.snackbar.open(this.trans.instant('admin.web.error-saving'), '', { duration: 2000 });
                    }
                });
        }
    }

    private parseCsvToUserNumbers(text: string): string[] {
        const clean = text.replace(/^\uFEFF/, '');
        const lines = clean.split(/\r?\n/);
        const result: string[] = [];

        for (const line of lines) {
            const trimmed = (line ?? '').trim();
            if (!trimmed) continue;

            const cell = trimmed.split(/[;,]/)[0]?.trim();
            if (!cell) continue;

            result.push(cell);
        }

        return result;
    }
}
